import { useCallback, useEffect, useState } from 'react';

import type { InboxItem } from '../api';
import { archiveInboxItem, fetchInbox, markAllInboxRead, markInboxItemRead } from '../api';
import { t } from '../i18n';

type InboxView = 'all' | 'unread' | 'archived';

interface InboxPanelProps {
  /** Which person reads this inbox; only ever the signed-in one. */
  viewerName: string;
  onClose: () => void;
  onOpenPage: (pageId: string) => void;
  /** Reported back so the sidebar badge follows. */
  onUnreadChange: (unread: number) => void;
}

/**
 * The inbox: what somebody mentioned you in, replied to, or asked you for.
 * It slides over the sidebar, as in the prototype.
 */
export function InboxPanel({
  viewerName,
  onClose,
  onOpenPage,
  onUnreadChange,
}: InboxPanelProps): React.ReactElement {
  const [items, setItems] = useState<InboxItem[]>([]);
  const [view, setView] = useState<InboxView>('all');
  const [mentionsOnly, setMentionsOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    return fetchInbox({ view, mentionsOnly })
      .then((list) => {
        setItems(list.items);
        onUnreadChange(list.unread);
        setError(null);
      })
      .catch(() => setError(t('error.generic')))
      .finally(() => setLoading(false));
  }, [view, mentionsOnly, onUnreadChange]);

  useEffect(() => {
    void load();
  }, [load]);

  const open = (item: InboxItem): void => {
    void markInboxItemRead(item.id)
      .then(() =>
        setItems((rows) => rows.map((row) => (row === item ? { ...row, read: true } : row))),
      )
      .catch(() => setError(t('error.generic')));
    if (item.pageId) onOpenPage(item.pageId);
  };

  return (
    <div className="inbox" role="dialog" aria-modal="true" aria-label={t('inbox.title')}>
      <div className="inbox__head">
        <h2 className="inbox__title">{t('inbox.title')}</h2>
        <button
          type="button"
          className="nb inbox__allread"
          onClick={() => void markAllInboxRead().then(load)}
        >
          {t('inbox.markAllRead')}
        </button>
        <button
          type="button"
          className="nb row inbox__close"
          aria-label={t('inbox.close')}
          onClick={onClose}
        >
          ×
        </button>
      </div>

      <div className="inbox__filters">
        <div className="inbox__tabs" role="tablist" aria-label={t('inbox.views')}>
          {(['all', 'unread', 'archived'] as InboxView[]).map((entry) => (
            <button
              key={entry}
              type="button"
              role="tab"
              aria-selected={view === entry}
              className={view === entry ? 'inbox__tab nb is-on' : 'inbox__tab nb'}
              onClick={() => setView(entry)}
            >
              {t(`inbox.view.${entry}` as 'inbox.view.all')}
            </button>
          ))}
        </div>
        <button
          type="button"
          aria-pressed={mentionsOnly}
          className={mentionsOnly ? 'inbox__pill nb is-on' : 'inbox__pill nb'}
          onClick={() => setMentionsOnly((current) => !current)}
        >
          {t('inbox.mentionsOnly')}
        </button>
      </div>

      <p className="inbox__viewer">{t('inbox.viewingAs', { name: viewerName })}</p>

      {error ? (
        <p className="inbox__error" role="alert">
          {error}
        </p>
      ) : null}

      <ul className="inbox__list">
        {loading ? null : items.length === 0 ? (
          <li className="inbox__empty">{t('inbox.empty')}</li>
        ) : (
          items.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                className={item.read ? 'inbox__item nb' : 'inbox__item nb is-unread'}
                onClick={() => open(item)}
              >
                <span className="inbox__avatar" aria-hidden="true">
                  {initialsOf(item.actorName)}
                </span>
                <span className="inbox__body">
                  <span className="inbox__line">{headingOf(item)}</span>
                  {item.excerpt ? <span className="inbox__excerpt">{item.excerpt}</span> : null}
                </span>
                <span className="inbox__meta">
                  <span className="inbox__time">{stampOf(item.createdAt)}</span>
                  {item.read ? null : <span className="inbox__dot" aria-hidden="true" />}
                </span>
              </button>
              <button
                type="button"
                className="nb inbox__archive"
                aria-label={t('inbox.archive', { name: item.actorName ?? '' })}
                onClick={() => void archiveInboxItem(item.id, true).then(load)}
              >
                ×
              </button>
            </li>
          ))
        )}
      </ul>
    </div>
  );
}

/** One line telling what happened, in the words of the prototype. */
function headingOf(item: InboxItem): string {
  const who = item.actorName ?? t('inbox.someone');
  const where = item.pageTitle ?? t('inbox.somewhere');
  switch (item.kind) {
    case 'mention':
      return t('inbox.heading.mention', { who, where });
    case 'reply':
      return t('inbox.heading.reply', { who, where });
    case 'assigned':
      return t('inbox.heading.assigned', { who, where });
    case 'access_requested':
      return t('inbox.heading.access', { who, where });
  }
}

/** "09:12" today, "Yesterday", or a date, as in the prototype's inbox. */
function stampOf(value: string): string {
  const when = new Date(value);
  const now = new Date();
  const sameDay = when.toDateString() === now.toDateString();
  if (sameDay) {
    return when.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  }
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (when.toDateString() === yesterday.toDateString()) return t('inbox.yesterday');
  return when.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function initialsOf(name: string | null): string {
  if (!name) return '?';
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
}
